import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  apiAuthenticateHandoff,
  apiAuthenticateSession,
  apiFeatureFlags,
  apiGenerateSession,
  apiLog,
  apiLogin,
  apiSettings,
} from 'tsx/constants/api';
import { APPLICATION_TITLE } from 'tsx/constants/headerMenu';
import { get, post } from 'tsx/libs/genericAction';

import { createThunks, Params } from '~libs/reduxUtils';

export const typePrefix = 'login';

export const setTitle = (title: string) => {
  document.title = `${title} - ${APPLICATION_TITLE}`;
};

export interface LoginRejectedPayload {
  message: string;
  reason?: string;
}

/**
 * Custom login thunk that uses rejectWithValue to preserve the API `reason`
 * field from 401 responses (used for reCAPTCHA fallback logic).
 */
export const login = createAsyncThunk<any, Params | undefined, { rejectValue: LoginRejectedPayload }>(
  `${typePrefix}/login`,
  async (params, { rejectWithValue }) => {
    try {
      // Call axios directly (not genericAction.post) so the API `reason` survives —
      // sendRequest re-throws a plain Error and strips response.data.reason.
      const response = await axios.post(apiLogin, params);
      return response.data ?? true;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const { message, reason } = error.response.data ?? {};
          return rejectWithValue({
            message:
              message ??
              'Request was rejected on the server with an unknown reasoning. Please contact your system administrator',
            reason,
          });
        }
        // No response received (e.g. API unreachable). Mirror buildErrorResponse's friendly
        // ERR_NETWORK string so login network-error UX stays consistent with the rest of the app.
        if (error.code === 'ERR_NETWORK') {
          return rejectWithValue({ message: 'Network error: No response received from API. Please contact your administrator' });
        }
      }
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return rejectWithValue({ message });
    }
  },
);

const config = [
  { actionType: 'authenticate', apiPath: apiAuthenticateSession, method: post },
  { actionType: 'authenticateHandoff', apiPath: apiAuthenticateHandoff, method: post },
  { actionType: 'generateSession', apiPath: apiGenerateSession, method: get },
  {
    actionType: 'getFeatureFlag',
    apiPath: (params: Params) => `${apiFeatureFlags}/${params.flag}`,
    method: get,
  },
  {
    actionType: 'getFeatureFlags',
    apiPath: apiFeatureFlags,
    method: get,
  },
  { actionType: 'getSettings', apiPath: apiSettings, method: get },
  { actionType: 'log', apiPath: apiLog, method: post },
];

export const {
  authenticate,
  authenticateHandoff,
  generateSession,
  getFeatureFlag,
  getFeatureFlags,
  getSettings,
  log,
} = createThunks(typePrefix, config);
